import {
  Chip,
  Container,
  Divider,
  ListItem,
  ListItemText,
  Paper,
  Stack,
} from '@mui/material'
import { useTranslation } from 'react-i18next'
import { FormProvider, useForm } from 'react-hook-form'
import {
  PluginCtuNorway,
  UpdateCtuNorwayDocument,
  GetCtuNorwayPluginDocument,
} from '../../../../generated/graphql'
import { PluginsForm } from '../../plugins/plugins-form'
import { useMutation, useQuery } from '@apollo/client'
import { usePageTitle } from '../../../../hooks/title'
import { DatePickerInput } from '../../../../inputs/date-picker-input'
import { TextFieldInput } from '../../../../inputs/text-field-input'
import { SectionHeader } from '@sitoo/mui-components'
import { getCurrencyName } from '../../../../i18n/currencies'
import { getCountryName } from '../../../../i18n/countries'

type Form = PluginCtuNorway

export const validateCtuSettings = (plugin: PluginCtuNorway | undefined) => {
  return plugin && Object.values(plugin).every(Boolean)
}

export const CtuNorwaySettings = () => {
  const { t } = useTranslation(['shared', 'apps'])
  usePageTitle(t('shared:menu.ctu_norway'))

  const defaultValues = {
    companyId: null,
    companyName: null,
    currencyCode: 'NOK',
    taxRegistrationNumber: null,
    taxRegistrationCountry: 'NO',
    dateFiscalFirst: null,
    dateFiscalSecond: null,
  } satisfies Form

  const formContext = useForm<Form>({ defaultValues })
  const { control, formState, getValues } = formContext
  const { isSubmitting } = formState

  const { data } = useQuery(GetCtuNorwayPluginDocument, {
    fetchPolicy: 'cache-and-network',
  })

  const [updateSettings, { loading: isLoading }] = useMutation(
    UpdateCtuNorwayDocument,
  )

  const currencyCode = getValues('currencyCode')
  const isInvalidCurrency = currencyCode !== defaultValues.currencyCode
  const countryName = getValues('taxRegistrationCountry')
  const isInvalidCountry = countryName !== defaultValues.taxRegistrationCountry

  const onSubmit = async (formData: Form) => {
    const data: Form = {
      ...formData,
      taxRegistrationCountry: defaultValues.taxRegistrationCountry,
      currencyCode: defaultValues.currencyCode,
    }

    const { data: response } = await updateSettings({ variables: { data } })

    return response?.updateCtuNorway
  }

  return (
    <Container>
      <Paper>
        <FormProvider {...formContext}>
          <PluginsForm
            onSubmit={onSubmit}
            serverData={data?.ctuNorwayPlugin}
            isLoading={isLoading}
          >
            <SectionHeader variant="transparent">
              {t('apps:ctu_norway.settings_tab')}
            </SectionHeader>

            <Stack gap={2} sx={{ p: 2, pt: 1 }}>
              <TextFieldInput
                name="companyId"
                control={control}
                label={t('apps:ctu_norway.company_id_label')}
                helperText={t('apps:ctu_norway.company_id_helper')}
                disabled={isSubmitting}
              />

              <TextFieldInput
                name="taxRegistrationNumber"
                control={control}
                label={t('apps:ctu_norway.tax_registration_number_label')}
                helperText={t('apps:ctu_norway.tax_registration_number_helper')}
                disabled={isSubmitting}
              />

              <TextFieldInput
                name="companyName"
                control={control}
                label={t('apps:ctu_norway.company_name_label')}
                helperText={t('apps:ctu_norway.company_name_helper')}
                disabled={isSubmitting}
              />
            </Stack>

            <Divider />

            <ListItem component="div" divider>
              <ListItemText
                primary={t('apps:ctu_norway.currency_code_label')}
                secondary={
                  <Chip
                    variant="outlined"
                    color={isInvalidCurrency ? 'red' : 'gray'}
                    label={
                      currencyCode
                        ? `${currencyCode}, ${getCurrencyName(currencyCode)}`
                        : currencyCode
                    }
                  />
                }
              />
            </ListItem>

            <ListItem component="div" divider>
              <ListItemText
                primary={t('apps:ctu_norway.tax_country_code_label')}
                secondary={
                  <Chip
                    variant="outlined"
                    color={isInvalidCountry ? 'red' : 'gray'}
                    label={
                      countryName ? getCountryName(countryName) : countryName
                    }
                  />
                }
              />
            </ListItem>

            <Stack gap={2} sx={{ p: 2, pt: 1 }}>
              <DatePickerInput
                control={control}
                name="dateFiscalFirst"
                label={t('apps:ctu_norway.date_fiscal_first_label')}
                helperText={t('apps:ctu_norway.date_fiscal_first_helper')}
                transform={{
                  output: (value) => value?.startOf('day').toJSON(),
                }}
              />

              <DatePickerInput
                control={control}
                name="dateFiscalSecond"
                label={t('apps:ctu_norway.date_fiscal_second_label')}
                helperText={t('apps:ctu_norway.date_fiscal_second_helper')}
                transform={{
                  output: (value) => value?.endOf('day').toJSON(),
                }}
              />
            </Stack>
          </PluginsForm>
        </FormProvider>
      </Paper>
    </Container>
  )
}
